import { graphql, useFragment } from 'react-relay';
import { useDispatch } from 'react-redux';
import { useState, type FC, type MouseEvent, type KeyboardEvent } from 'react';

import { updateUriRef } from '../../../actions/filterActions.ts';
import { useRingMeasurementType } from '../../hooks/ringSize/useRingMeasurementType.ts';
import { RING_SIZE_FILTER_NAME, RING_SIZE_US } from '../../../constants/ringSize.ts';
import { trackSeeMoreClick } from '../../../utils/tracking/searchBrowse/filterTracking.ts';

import { FormattedMessage } from 'dibs-react-intl';
import { Link } from 'dibs-elements/exports/Link.tsx';
import { getFilterValues } from '../sbSharedRefineMenuHelpers.ts';
import { SbSharedRefineMenuRingSizeUnitSelector } from './SbSharedRefineMenuRingSizeUnitSelector.jsx';
import dibsCss from 'dibs-css';
import classnames from 'classnames';
import { useCurrency } from '../../../shared/hooks/useCurrency.ts';
import styles from './SbSharedRefineMenuRingSize.scss';

import { type SbSharedRefineMenuRingSize_filters$key } from './__generated__/SbSharedRefineMenuRingSize_filters.graphql.ts';
import { type SbSharedRefineMenuRingSize_itemSearch$key } from './__generated__/SbSharedRefineMenuRingSize_itemSearch.graphql.ts';

const COLLAPSED_RING_SIZE_COUNT = 20;

const filtersFragment = graphql`
    fragment SbSharedRefineMenuRingSize_filters on SearchBrowseFilter @relay(plural: true) {
        name
        totalCount
        localizedFilterName
        values {
            displayName
            urlLabel
            linkReference
            hexCode
            properties {
                mm
                us
            }
        }
    }
`;

const itemSearchFragment = graphql`
    fragment SbSharedRefineMenuRingSize_itemSearch on ItemSearchQueryConnection {
        ...useRingMeasurementType_itemSearch
        ...SbSharedRefineMenuRingSizeUnitSelector_itemSearch
        appliedFilters {
            name
            values {
                urlLabel
                displayName
                linkReference
                properties {
                    mm
                    us
                }
            }
        }
    }
`;

type Props = {
    filters: SbSharedRefineMenuRingSize_filters$key;
    itemSearch: SbSharedRefineMenuRingSize_itemSearch$key;
};

export const SbSharedRefineMenuRingSize: FC<Props> = ({
    filters: filtersRef,
    itemSearch: itemSearchRef,
}) => {
    const filters = useFragment(filtersFragment, filtersRef);
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const { currency } = useCurrency();

    const dispatch = useDispatch();
    const [isExpanded, setIsExpanded] = useState(false);
    const ringMeasurementType = useRingMeasurementType({ currency, itemSearch });
    const ringSizeFilters = getFilterValues(filters, RING_SIZE_FILTER_NAME);
    const appliedRingSizeFilters = getFilterValues(
        itemSearch?.appliedFilters,
        RING_SIZE_FILTER_NAME
    );
    const selectedRingSizes: Record<string, boolean> = {};
    (appliedRingSizeFilters || []).forEach(filter => {
        if (filter.urlLabel) {
            selectedRingSizes[filter.urlLabel] = true;
        }
    });

    const onClickRingSize = (
        filterValue: (typeof ringSizeFilters)[number],
        isDeselected: boolean,
        event: MouseEvent | KeyboardEvent
    ): void => {
        // Click on selected ring size should lead to deselection event
        dispatch(
            updateUriRef({
                filterName: RING_SIZE_FILTER_NAME,
                filterValue,
                isDeselected,
                event,
            })
        );
    };

    return (
        <>
            <SbSharedRefineMenuRingSizeUnitSelector currency={currency} itemSearch={itemSearch} />
            <div
                className={classnames(
                    styles.ringSizeFilterContainer,
                    dibsCss.grid,
                    dibsCss.gapXsmall
                )}
            >
                {ringSizeFilters.map((filter, index) => {
                    const isSelected = selectedRingSizes[filter.urlLabel || ''];
                    return isExpanded || index < COLLAPSED_RING_SIZE_COUNT ? (
                        <div
                            key={filter.urlLabel}
                            className={classnames(
                                styles.ringSizeFilterTile,
                                dibsCss.flex,
                                dibsCss.itemsCenter,
                                dibsCss.justifyCenter,
                                dibsCss.cursorPointer,
                                dibsCss.borderSolid,
                                isSelected
                                    ? [dibsCss.border2, dibsCss.borderNoir]
                                    : [dibsCss.border, dibsCss.borderDolphin]
                            )}
                            onClick={e => {
                                onClickRingSize(filter, isSelected, e);
                            }}
                            onKeyDown={e => {
                                if (e.key === 'Enter') {
                                    onClickRingSize(filter, isSelected, e);
                                }
                            }}
                            role="button"
                            tabIndex={0}
                        >
                            {ringMeasurementType === RING_SIZE_US
                                ? filter?.properties?.us
                                : filter?.properties?.mm}
                        </div>
                    ) : null;
                })}
            </div>
            <Link
                buyerLinkType="standardLink"
                className={styles.expandFilters}
                onClick={() => {
                    setIsExpanded(!isExpanded);
                    if (!isExpanded) {
                        trackSeeMoreClick(RING_SIZE_FILTER_NAME);
                    }
                }}
                ariaExpanded={isExpanded}
                dataTn={`see-${isExpanded ? 'less' : 'more'}`}
            >
                <FormattedMessage
                    id="sb.SbSharedRefineMenuRingSize.seeMore"
                    defaultMessage={`{isExpanded, select,
                                true {See Less}
                                other {See More}
                            }`}
                    values={{ isExpanded }}
                />
            </Link>
        </>
    );
};
